import React, { useState, useEffect} from "react";
import { useNavigate } from "react-router-dom";

import EmailAutoComplete from "../autoComplete";
import Collapse from "src/pages/asset/components/animation/collapse";
import { Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";

import { Input } from "src/components/input";
import Button from "src/components/button";
import { useSelector } from "react-redux";
import ComposeEditor from "../editor/index";
import Tooltip from "src/components/tooltip";
import SwitchButton from "src/components/switchButton";
import Toast from "src/components/toast";
import { getIntegrationForCategory } from "src/legacy/redux/actions/integrations.jsx";

import { EDITOR_ID, EMAIL_CONFIG } from "../../constants";

import _ from "lodash";
import cx from "classnames";
import styles from "./body.module.scss";

interface EmailBodyProps {
  emailConfigState: any;
  uploading: boolean;
  onSubjectChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEditorMessageChange: (message: string) => void;
  onRecipientsChange: (recipients: any, type: string) => void;
  applyWidgetToMessage: (message: string) => void;
  onClose: () => void;
  onSend: () => void;
  disabled? : any;
  emailRequestState : any;
  onBulkEmailChange : (enabled : boolean) => void;
  processAssets : any;
  getCollectionIcon : any;
  isDealRoom: boolean;
}

const EmailBody: React.FC<EmailBodyProps> = ({
  emailConfigState,
  uploading,
  onRecipientsChange,
  onSubjectChange,
  onEditorMessageChange,
  applyWidgetToMessage,
  onClose,  
  disabled,
  onSend,
  emailRequestState,
  onBulkEmailChange,
  processAssets,
  getCollectionIcon,
  isDealRoom
}) => {
  const navigate = useNavigate();

  const [ccIsExpanded, setccIsExpanded] = useState(false);
  const [bccIsExpanded, setbccIsExpanded] = useState(false);
  const [subject, setSubject] = useState(emailConfigState.subject || "");
  const [enabledBulkEmail, setEnabledBulkEmail] = useState(true);
  const [showBulkEmailDisclaimar, setShowBulkEmailDisclaimar] = useState(false);

  const state = useSelector((state: any) => state); 
  const emailIntegration = getIntegrationForCategory(state, "email");

  const creating = useSelector((state: any) =>
    _.get(state, "conversations.conversation.creation.creating", false)
  );

  const {isDistributionList} = emailConfigState;
  const { toAddresses, ccAddresses, bccAddresses} = emailRequestState;

  const handleCcClick = () => {
    setccIsExpanded(!ccIsExpanded);
  };

  const handleBccClick = () => {
    setbccIsExpanded(!bccIsExpanded);
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSubjectChange(e);
  }

  const calculateBulkEmailDisclaimar = () => {
    const canShowBulkEmailDisclaimar = canRenderBulkEmailDisclaimar();
    setShowBulkEmailDisclaimar(canShowBulkEmailDisclaimar); 
  };

  const handleChange = (field) => {
    if (
      field === "canDownload" ||
      field === "canReshare" ||
      field === "engage" ||
      field === "downloadEditable" ||
      field === "enabledBulkEmail"
    ) {
      if (field === "enabledBulkEmail") {
        onBulkEmailChange(!enabledBulkEmail); 
        if (!enabledBulkEmail) {
          calculateBulkEmailDisclaimar();
        } else {
          setShowBulkEmailDisclaimar(false); 
        }   

        setEnabledBulkEmail(!enabledBulkEmail);
      }
    }
  };

  const renderBulkEmailDisclaimarType = () => {

    if (_.size(ccAddresses) > 0 && _.size(bccAddresses) > 0) {
      return "CC and BCC recipients";
    }
  
    if (_.size(ccAddresses) > 0 && _.size(bccAddresses) === 0) {
      return "CC recipients";
    }
  
    if (_.size(ccAddresses) === 0 && _.size(bccAddresses) > 0) {
      return "BCC recipients";
    }
  
    return null; 
  };
  
  const calculateBulkEmailCount = () => {
    let count = 0;

    if (isDistributionList) {
      toAddresses.forEach((contact) => {
        if (contact.type === "contact_list") {
          const contactDetails = _.find(emailConfigState.contacts, { id: contact.value });
          count += contactDetails?.contactCount || 0;
        } else {
          count += 1;
        }
      });
      return count;
    } else {
      return _.size(toAddresses);
    }
  };

  const canRenderBulkEmailDisclaimar = () => {
    const toSize = _.size(toAddresses);
    const ccSize = _.size(ccAddresses);
    const bccSize = _.size(bccAddresses);
    const hasMultipleRecipients = toSize > 1 || isDistributionList;

    if(!enabledBulkEmail) {
      return false;
    }
    
    if((toSize > 2 || hasMultipleRecipients) && (ccSize > 0 || bccSize > 0)){
      return true;
    }

    return false;
  
  };

  const renderEmailErrorMessage = () => {
    if (!emailIntegration) return null;
  
    let emailProvider = emailIntegration.type;
    if (emailIntegration.type === "gmail") {
      emailProvider = "Gmail";
    } else if (emailIntegration.type === "outlook") {
      emailProvider = "Outlook";
    }
  
    return (
      <span className={styles.errorMessage}>
        <span className={styles.alertHeading}>
          <i className="paperflite-danger"></i> Psst!
        </span>
        Your authentication to {emailProvider} has expired and needs to be renewed. 
        Please re-connect your {emailProvider} to send from your email.{" "}
        <a onClick={handleRedirect}>Click here to authenticate</a>
      </span>
    );
  };
  
  
  const handleRedirect = () => {
    onClose();
    navigate(`/settings/integrations/${emailIntegration ? emailIntegration.type : ""}`);
  };

  useEffect(() => {
    
    return () => {
      setShowBulkEmailDisclaimar(false);
      toast.dismiss();
    };
  }, []);

  useEffect(() => {
    if (showBulkEmailDisclaimar) {
      toast(
          <Toast.Failure
            className={styles.failureToast}
            message={`Since youre sending this as an Individual Email to ${_.size(toAddresses)} ${_.size(toAddresses) > 1 ? "recipients" : "recipient"}, ${renderBulkEmailDisclaimarType()} will receive this email ${calculateBulkEmailCount()} times.`}
            />,
        {
          position: "bottom-right",
          onClose: () => setShowBulkEmailDisclaimar(false),
          autoClose: false,
        }
      );
    }

  }, [showBulkEmailDisclaimar]);


  useEffect(() => {
    calculateBulkEmailDisclaimar();
  }, [toAddresses, ccAddresses, bccAddresses]);

  useEffect(() => {
    setSubject(emailConfigState.subject || "");
  }, [emailConfigState.subject]);

  const getEntityType = () => {
    return isDealRoom ? "contact" : "both";
  }

  return (
    <section className={styles.body}>
      <div className={styles.headerContainer}>
        <h3 className={styles.heading}>compose</h3>
        <span className={styles.subheading}>quick send</span>
      </div>

      {emailIntegration &&
        emailIntegration.state.code == "auth_error" &&
        renderEmailErrorMessage()}

      {!emailIntegration && (
        <div className={styles.errorMessage}>
          <span className={styles.alertHeading}>
            <i className="paperflite-danger"></i> Psst!
          </span>
          This email will be sent from share@paperflite.email. Please connect
          your Outlook/Gmail/Email account to send from your email.{" "}
          <a onClick={handleRedirect}>Configure here</a>
        </div>
      )}

      <div className={styles.recipentFields}>
        <div className={styles.email}>
          <label>To</label>
          <EmailAutoComplete
            value={emailRequestState.toAddresses || []}
            className={styles.emailTo}
            inputPlaceholder=""
            entityType={getEntityType()}
            onChange={(tokens) => onRecipientsChange(tokens, EMAIL_CONFIG.TO)}
            contactSource="quicksend"
            width="calc(35vw - 3.75rem)"
          />
          <div className={styles.options}>
            <span
              onClick={() => handleCcClick()}
              className={cx(styles.item, { [styles.active]: ccIsExpanded })}
            >
              Cc
            </span>
            <span
              onClick={() => handleBccClick()}
              className={cx(styles.item, { [styles.active]: bccIsExpanded })}
            >
              Bcc
            </span>
          </div>
        </div>

        <Collapse isOpen={ccIsExpanded}>
          <div className={styles.panel}>
            <div className={styles.gradientLine} />
            <div className={styles.email}>
              <label>Cc</label>
              <EmailAutoComplete
                className={styles.emailTo}
                inputPlaceholder=""
                entityType={getEntityType()}
                value={emailRequestState.ccAddresses || []}     
                onChange={(tokens) =>
                  onRecipientsChange(tokens, EMAIL_CONFIG.CC)
                }
                contactSource="quicksend"
                width="calc(35vw - 3.75rem)"
              />
            </div>
          </div>
        </Collapse>

        <Collapse isOpen={bccIsExpanded}>
          <div className={styles.panel}>
            <div className={styles.gradientLine} />
            <div className={cx(styles.email, styles.bcc)}>
              <label>Bcc</label>
              <EmailAutoComplete
                className={styles.emailTo}
                inputPlaceholder=""
                entityType={getEntityType()}
                value={emailRequestState.bccAddresses || []}     
                onChange={(tokens) =>
                  onRecipientsChange(tokens, EMAIL_CONFIG.BCC)
                }
                contactSource="quicksend"
                width="calc(35vw - 3.75rem)"
              />
            </div>
          </div>
        </Collapse>
      </div>
      <div>
        {_.size(toAddresses) > 1 && (
          <Col sm={12} lg={12} xl={12} md={12} className={styles.column}>
            <div className={styles.permission}>
              <div className={styles.bulkMail}>
                <Tooltip
                  placement="bottom"
                  content="This feature lets you send content as individual emails to each recipient. Turn this OFF to send as an email with all recipients visible in the “To” field."
                >
                  <label>
                    Send as individual emails
                    <i className="fa-solid fa-circle-exclamation" />
                  </label>
                </Tooltip>
                <div className={styles.bulkEmailToggle}>
                  <SwitchButton
                    id={`enableBulkEmail_${new Date().getTime()}`}
                    checked={enabledBulkEmail}
                    handleCheck={() => handleChange("enabledBulkEmail")}
                  />
                </div>
              </div>
            </div>
          </Col>
        )}
      </div>
      <div className={styles.subject}>
        <label>Subject</label>
        <Input
          className={styles.subjectInput}
          placeholder=""
          value={subject}
          onChange={handleSubjectChange}
        />
      </div>

      <div className={styles.editor}>
        <ComposeEditor
          editorId={EDITOR_ID}
          emailConfigState={emailConfigState}
          onChange={onEditorMessageChange}
          applyWidgetToMessage={applyWidgetToMessage}
          processAssets={processAssets}
          getCollectionIcon={getCollectionIcon}
        />
      </div>

      <div className={cx(styles.row, styles.actions)}>
        <Button
          text={creating ? "sending" : "send"}
          variant="primary"
          disabled={disabled}
          onClick={onSend}
          icon="paperflite-send"
        />
        <Button
          text="cancel"
          variant="secondary"
          disabled={uploading}
          onClick={onClose}
        />
      </div>
    </section>
  );
};

export default React.memo(EmailBody);